import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import type {
  ScheduledEmailReport,
  ScheduledEmailReportInput,
  SendPreviewResponse,
} from "../types";
import type { RootState } from "../../../common/state/store";

interface ReportsState {
  items: ScheduledEmailReport[];
  isLoading: boolean;
  isSubmitting: boolean;
  isSendingPreview: number | null; // report id currently sending, or null
  error: string | null;
  lastPreviewResult: SendPreviewResponse | null;
}

const initialState: ReportsState = {
  items: [],
  isLoading: false,
  isSubmitting: false,
  isSendingPreview: null,
  error: null,
  lastPreviewResult: null,
};

// Strapi v4 envelopes responses as { data, meta } on listing/single. Single
// item endpoints return { data: {...record} } via our customised controller.
// We extract the bare record so the store works in plain TS.
const hasDataProp = (data: unknown): data is { data: unknown } =>
  typeof data === "object" && data !== null && "data" in data;

const unwrapItem = (data: unknown): ScheduledEmailReport =>
  (hasDataProp(data) ? data.data : data) as ScheduledEmailReport;
const unwrapList = (data: unknown): ScheduledEmailReport[] => {
  if (Array.isArray(data)) return data as ScheduledEmailReport[];
  if (hasDataProp(data) && Array.isArray(data.data)) return data.data as ScheduledEmailReport[];
  return [];
};

export const fetchReports = createAsyncThunk("reports/fetchAll", async () => {
  const res = await axios.get(apiUrls.scheduledEmailReports.list, {
    params: { "pagination[pageSize]": 100 },
  });
  return unwrapList(res.data);
});

export const createReport = createAsyncThunk(
  "reports/create",
  async (input: ScheduledEmailReportInput) => {
    const res = await axios.post(apiUrls.scheduledEmailReports.list, { data: input });
    return unwrapItem(res.data);
  },
);

export const updateReport = createAsyncThunk(
  "reports/update",
  async (args: { id: number; input: Partial<ScheduledEmailReportInput> }) => {
    const res = await axios.put(apiUrls.scheduledEmailReports.item(args.id), {
      data: args.input,
    });
    return unwrapItem(res.data);
  },
);

export const deleteReport = createAsyncThunk("reports/delete", async (id: number) => {
  await axios.delete(apiUrls.scheduledEmailReports.item(id));
  return id;
});

export interface SendPreviewRejectPayload {
  status: number | null;
  message: string;
}

export const sendPreviewReport = createAsyncThunk<
  SendPreviewResponse,
  { id: number; startDate: string; endDate: string; testRecipient?: string },
  { rejectValue: SendPreviewRejectPayload }
>("reports/sendPreview", async (args, { rejectWithValue }) => {
  const body: Record<string, unknown> = {
    startDate: args.startDate,
    endDate: args.endDate,
    // Test sends always include the LLM narrative so the email matches what
    // the cron would actually deliver. The backend defaults to false (preview
    // cost optimisation), but an explicit user-triggered Test send should not
    // surprise them with a "narrative missing" body.
    withNarrative: true,
  };
  if (args.testRecipient) body.testRecipient = args.testRecipient;
  try {
    const res = await axios.post(apiUrls.scheduledEmailReports.sendPreview(args.id), {
      data: body,
    });
    return res.data as SendPreviewResponse;
  } catch (err) {
    // Surface the HTTP status so the UI can branch on 429 (rate limit) vs
    // other failures. Without this the thunk's default error serialiser loses
    // the status code and the UI can only show a generic "failed" toast.
    if (axios.isAxiosError(err)) {
      const status = err.response?.status ?? null;
      const message =
        (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message ??
        err.message;
      return rejectWithValue({ status, message });
    }
    return rejectWithValue({ status: null, message: (err as Error).message });
  }
});

const slice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLastPreview: (state) => {
      state.lastPreviewResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action: PayloadAction<ScheduledEmailReport[]>) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? "fetchReports failed";
      })
      .addCase(createReport.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(createReport.fulfilled, (state, action: PayloadAction<ScheduledEmailReport>) => {
        state.isSubmitting = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createReport.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message ?? "createReport failed";
      })
      .addCase(updateReport.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(updateReport.fulfilled, (state, action: PayloadAction<ScheduledEmailReport>) => {
        state.isSubmitting = false;
        state.items = state.items.map((r) => (r.id === action.payload.id ? action.payload : r));
      })
      .addCase(updateReport.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message ?? "updateReport failed";
      })
      .addCase(deleteReport.fulfilled, (state, action: PayloadAction<number>) => {
        state.items = state.items.filter((r) => r.id !== action.payload);
      })
      .addCase(sendPreviewReport.pending, (state, action) => {
        state.isSendingPreview = action.meta.arg.id;
        state.lastPreviewResult = null;
      })
      .addCase(sendPreviewReport.fulfilled, (state, action) => {
        state.isSendingPreview = null;
        state.lastPreviewResult = action.payload;
      })
      .addCase(sendPreviewReport.rejected, (state, action) => {
        state.isSendingPreview = null;
        state.error = action.payload?.message ?? action.error.message ?? "sendPreview failed";
      });
  },
});

export const { clearError, clearLastPreview } = slice.actions;
export const reportsReducer = slice.reducer;

export const reportsSelector = (state: RootState) =>
  (state as RootState & { reports: ReportsState }).reports.items;
export const reportsIsLoadingSelector = (state: RootState) =>
  (state as RootState & { reports: ReportsState }).reports.isLoading;
export const reportsIsSubmittingSelector = (state: RootState) =>
  (state as RootState & { reports: ReportsState }).reports.isSubmitting;
export const reportsIsSendingPreviewSelector = (state: RootState) =>
  (state as RootState & { reports: ReportsState }).reports.isSendingPreview;
export const reportsErrorSelector = (state: RootState) =>
  (state as RootState & { reports: ReportsState }).reports.error;
export const reportsLastPreviewSelector = (state: RootState) =>
  (state as RootState & { reports: ReportsState }).reports.lastPreviewResult;

export const reportByIdSelector =
  (id: number | null) =>
  (state: RootState): ScheduledEmailReport | undefined => {
    if (id == null) return undefined;
    return (state as RootState & { reports: ReportsState }).reports.items.find((r) => r.id === id);
  };
